import { useQuery } from "@tanstack/react-query";
import { DocumentContentService } from "../../../../services/document/document-content.service";
import { usePublicDocumentInfo } from "./use-public-documents-info";
import { useMemo } from "react";
import _ from "lodash";
import { useSearchParams } from "../../../../hook/use-search-params";

export const usePublicDocumentContent = () => {
  const { data: docPublicData } = usePublicDocumentInfo();
  const { searchParams } = useSearchParams();

  const fileId = docPublicData?.fileId;

  const { data, isLoading } = useQuery({
    queryKey: ["publish-document-content", fileId],
    queryFn: () => DocumentContentService.getList("", fileId),
    enabled: !!fileId,
    staleTime: Infinity,
  });

  const maxVersion = useMemo(() => (!data ? 0 : Math.max(...data?.map((e: any) => e.publishVersion))), [data]);

  const previousVersion = !isNaN(Number.parseInt(searchParams.get("previousVersion") ?? ""))
    ? Number.parseInt(searchParams.get("previousVersion") ?? "")
    : maxVersion - 1;

  const currentVersion = !isNaN(Number.parseInt(searchParams.get("currentVersion") ?? ""))
    ? Number.parseInt(searchParams.get("currentVersion") ?? "")
    : maxVersion;

  const groupedData = useMemo(() => _.groupBy(data, "publishVersion"), [data]);

  const versionData = useMemo(() => {
    if (!data) return [];

    // Extract publishVersion, remove duplicates, and sort in descending order
    const distinctVersions = [...new Set(data.map((e: any) => e.publishVersion))];

    return distinctVersions.sort((a: any, b: any) => b - a);
  }, [data]);

  const versionOptions = useMemo(() => versionData.map((e) => ({ label: `Phiên bản ${e}`, value: e })), [versionData]);

  const groupedContentIdData = useMemo(() => {
    if (!data) return {};
    const filteredData = data.filter(
      (e: any) => e.publishVersion === currentVersion || e.publishVersion === previousVersion,
    );

    const groupedData = _.groupBy(filteredData, "CustomId");
    return groupedData;
  }, [currentVersion, data, previousVersion]);

  const differenData = useMemo(() => {
    if (maxVersion < 2) return [];
    const clonedGroupContentIdData = _.cloneDeep(groupedContentIdData);
    const filteredObject = {
      ...Object.values(clonedGroupContentIdData).map((e) => {
        if (e.length === 1) {
          e[0].publishVersion !== maxVersion ? e.push({ Content: "" }) : e.unshift({ Content: "" });
        }
        if (e.length === 2 && e[0].publishVersion > e[1].publishVersion) {
          [e[0], e[1]] = [e[1], e[0]];
        }
        return { ...e };
      }),
    };
    const res: any[] = Object.values(filteredObject).filter((e: any) => e[0].Content !== e[1]?.Content);
    res.unshift([{}, {}]);
    return res.filter((r) => !r[1]?.Content?.includes("<table") && !r[0]?.Content?.includes("<table"));
  }, [groupedContentIdData, maxVersion]);

  const differenDataByVersion = useMemo(() => {
    if (maxVersion < 2) return [];
    const clonedGroupContentIdData = _.cloneDeep(groupedContentIdData);
    const filteredObject = {
      ...Object.values(clonedGroupContentIdData).map((e) => {
        if (e.length === 1) {
          e[0].publishVersion !== maxVersion ? e.push({ Content: "" }) : e.unshift({ Content: "" });
        }
        if (e.length === 2 && e[0].publishVersion > e[1].publishVersion) {
          [e[0], e[1]] = [e[1], e[0]];
        }
        return { ...e };
      }),
    };
    const res: any[] = Object.values(filteredObject).filter(
      (e: any) => e[0].Content !== e[1]?.Content,
      (e: any) => true,
    );
    res.unshift([{}, {}]);
    return res.filter((r) => !r[1]?.Content?.includes("<table") && !r[0]?.Content?.includes("<table"));
  }, [groupedContentIdData, maxVersion]);

  return {
    groupedData,
    differenData,
    isLoading,
    docPublicData,
    maxVersion,
    versionOptions,
    versionData,
    data,
    differenDataByVersion,
    previousVersion,
    currentVersion,
  };
};
